import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import { ExamGroup } from '../exam/ExamGroup';
import { PassagePane } from '../exam/PassagePane';
import { headingSlots, passageHasParagraphs } from '../exam/headings';
import { paint, unpaint } from '../exam/marks';
import { TYPE_LABEL, lengthCheck, recordingFor } from '../lib/review';
import type { Review, ReviewMark } from '../lib/review';
import type { AnswerState } from '../exam/useSitting';
import type { BankOption } from '../lib/tests';
import { clock } from '../lib/sitting';
import { writingPromptHtml } from '../exam/writing';
import { band as bandText, CRITERIA } from '../lib/results';
import { clearsAt, copyScript, wordCount } from '../lib/scripts';
import { formatDate } from '../lib/when';

/**
 * Going back over a Writing paper: the task as it was set, beside what the
 * candidate wrote, with the marker's bands and comments for it.
 *
 * Laid out as the exam lays it out — the task on the left, the answer on the
 * right — because the question a candidate is asking of their own essay is
 * "did this answer that?", and that needs both in view at once. The result
 * page already had the essay and the marks; what it never had was the task.
 *
 * The scripts are only here while they are kept. After that the essay pane
 * says when it went and how long it was, which is still worth knowing: a
 * script under the minimum loses marks for that alone.
 */
function WritingReviewView({ review, id }: { review: Review; id: string }) {
  const writing = review.writing!;
  const tasks = review.parts.map((p) => p.index).filter((i): i is 1 | 2 => i === 1 || i === 2);
  const [task, setTask] = useState<1 | 2>(tasks[0] ?? 1);
  const [copied, setCopied] = useState(false);

  const part = review.parts.find((p) => p.index === task) ?? review.parts[0]!;
  const mark = writing.marks.find((m) => m.task === task) ?? null;
  const essay = writing.essays[String(task)]?.trim() ?? '';
  const words = essay ? wordCount(essay) : (writing.scriptWords?.[task] ?? 0);
  const length = lengthCheck(task, words);
  const keptUntil = writing.scriptsClearedAt ? null : clearsAt(writing.markedAt, writing.releasedAt);
  const variant = review.test.variant === 'GENERAL' ? 'GENERAL' : 'ACADEMIC';

  async function copy() {
    if (await copyScript(essay, document.getElementById(`review-essay-${task}`))) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={<Link to={review.showsAnswers ? `/results/${id}` : `/my/results/${id}`}>← Result</Link>}
        title={review.test.title}
        meta={
          <p className="muted">
            {review.showsAnswers ? review.candidate.fullName : 'You'} · band {review.result.band ?? '—'}
          </p>
        }
      />

      {!review.showsAnswers && keptUntil && Object.values(writing.essays).some((e) => e?.trim()) && (
        <div className="review__notice">
          <Banner tone="info">
            Your writing is kept until <strong>{formatDate(keptUntil.toISOString())}</strong>. Copy it
            into a document if you want to keep it — your bands and your teacher’s comments stay here
            after that.
          </Banner>
        </div>
      )}

      {/* The two tasks, each with its band: the Writing version of the breakdown. */}
      <section className="review__tally">
        {tasks.map((t) => {
          const m = writing.marks.find((x) => x.task === t);
          return (
            <button
              key={t}
              type="button"
              className={`review__tallyItem review__tallyItem--pick ${t === task ? 'is-on' : ''}`}
              aria-pressed={t === task}
              onClick={() => setTask(t)}
            >
              <span className="review__tallyLabel">Task {t}</span>
              <span className="review__tallyScore">{m ? bandText(m.band) : '—'}</span>
            </button>
          );
        })}
      </section>

      <div className="review review--split">
        <div className="review__passage">
          {part.groups.map((group) => (
            <div key={group.id}>
              {group.bodyHtml && (
                <div
                  className="qbody xprompt"
                  dangerouslySetInnerHTML={{ __html: writingPromptHtml(group.bodyHtml, task, variant) }}
                />
              )}
              {group.imageUrl && (
                <figure className="qgroup__figure">
                  <img src={group.imageUrl} alt={`The figure for Task ${task}`} />
                </figure>
              )}
            </div>
          ))}
        </div>

        <div className="review__questions stack">
          <div className="review__length" data-short={length.short > 0 || undefined}>
            <span className="mono">{length.words} words</span>
            <span>
              {length.short > 0
                ? `${length.short} short of the ${length.minimum} Task ${task} asks for — that costs marks on its own.`
                : `Meets the ${length.minimum} Task ${task} asks for.`}
            </span>
          </div>

          <div>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <p className="eyebrow" style={{ marginBottom: 0 }}>
                {review.showsAnswers ? 'What they wrote' : 'What you wrote'}
              </p>
              {essay && (
                <button className="btn btn--ghost" onClick={() => void copy()}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              )}
            </div>
            <article className="marking__essay" id={`review-essay-${task}`} style={{ maxHeight: 'none' }}>
              {essay ? (
                essay.split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)
              ) : writing.scriptsClearedAt ? (
                <p className="muted">
                  This script was cleared on {formatDate(writing.scriptsClearedAt)}, two days after the
                  result was released. It ran to {words} words.
                </p>
              ) : (
                <p className="muted">Nothing was written for this task.</p>
              )}
            </article>
          </div>

          {mark ? (
            <div className="stack" style={{ gap: 'var(--s-3)' }}>
              <p className="eyebrow" style={{ marginBottom: 0 }}>
                How it was marked
              </p>
              {/* Each band with what it measures on this task, so "6.0" comes
                  with the question the examiner was asking. */}
              <dl className="review__criteria">
                {CRITERIA.map((c) => (
                  <div key={c.key} className="review__criterion">
                    <dt>
                      {c.label}
                      <span className="muted">{task === 1 ? c.task1 : c.task2}</span>
                    </dt>
                    <dd className="mono">{bandText(mark.criteria[c.key])}</dd>
                  </div>
                ))}
              </dl>
              {mark.comments && (
                <div>
                  <p className="eyebrow">{review.showsAnswers ? 'The marker wrote' : 'Your teacher wrote'}</p>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{mark.comments}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="muted">This task has not been marked yet.</p>
          )}
        </div>
      </div>
    </>
  );
}


/**
 * Going back over a paper that has been sat.
 *
 * The same panes the candidate answered on — the passage, the questions as
 * they were asked — with their own answers filled in and locked, and where a
 * rule can be certain, the sentence their answer came from.
 *
 * It shares the exam's rendering and none of its machinery. Every write a
 * sitting makes — an answer saved, a heartbeat, a submit — lives in
 * useSitting, which this screen does not use; the only thing it takes from
 * there is a type, which compiles to nothing. So no mistake here can reach a
 * live sitting.
 */

/** Nothing here changes anything: every control is locked. */
const LOCKED = () => undefined;

/** The speeds a recording can be slowed to, for a candidate who missed a word. */
const SPEEDS = [1, 0.75, 0.5] as const;

/**
 * An ordinary player for a recording that has already been sat.
 *
 * Deliberately not the exam's player. That one is driven by the server's
 * clock, has no pause and no scrubbing, and starts where the sitting says the
 * recording is — all of which is the point of an exam and none of which is the
 * point of going back over one. This is the browser's own control, with play,
 * pause and seeking, and a way to slow it down.
 *
 * Mounted afresh for each recording (keyed by its address), so changing part
 * stops the one that was playing rather than leaving it running behind a
 * different part's questions.
 */
type Speed = (typeof SPEEDS)[number];

function Recording({
  url,
  label,
  durationMs,
  note,
  speed,
  onSpeed,
}: {
  url: string;
  label: string;
  durationMs: number | null;
  note: string | null;
  /**
   * Held by the page, not here: this player is mounted afresh for each part,
   * and a candidate who slowed Part 2 down should not find Part 3 back at
   * full speed.
   */
  speed: Speed;
  onSpeed: (speed: Speed) => void;
}) {
  const audio = useRef<HTMLAudioElement>(null);

  // Set on the element each time, and as its default too: a browser that
  // reloads the media resets the rate to the default, so setting only the
  // current rate loses it the first time the candidate seeks.
  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    el.defaultPlaybackRate = speed;
    el.playbackRate = speed;
  }, [speed]);


  return (
    <section className="review__recording" aria-label={label}>
      <div className="review__recordingHead">
        <p className="review__recordingLabel">
          {label}
          {durationMs ? <span className="muted"> · {clock(durationMs)}</span> : null}
        </p>
        <div className="groupbar__chips" role="group" aria-label="Playback speed">
          <span className="eyebrow">Speed</span>
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              className={`chip ${speed === s ? 'chip--on' : ''}`}
              aria-pressed={speed === s}
              onClick={() => onSpeed(s)}
            >
              {s === 1 ? 'Normal' : `${s}×`}
            </button>
          ))}
        </div>
      </div>
      <audio
        ref={audio}
        className="review__audio"
        src={url}
        controls
        preload="metadata"
        controlsList="nodownload"
        onLoadedMetadata={(e) => {
          e.currentTarget.defaultPlaybackRate = speed;
          e.currentTarget.playbackRate = speed;
        }}
      />
      {note && <p className="muted review__recordingNote">{note}</p>}
    </section>
  );
}

export function ReviewPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [review, setReview] = useState<Review | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [partIndex, setPartIndex] = useState(1);
  /** The question whose evidence is showing, if any. */
  const [showing, setShowing] = useState<number | null>(null);
  /** How fast recordings play, kept across parts. */
  const [speed, setSpeed] = useState<Speed>(1);
  const passageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api
      .get<Review>(`/results/${id}/review`)
      .then(({ data }) => setReview(data))
      .catch((err) => setError(errorMessage(err, 'Could not open this paper')));
  }, [id]);

  const answers = useMemo(() => {
    const map = new Map<number, AnswerState>();
    for (const a of review?.answers ?? []) {
      map.set(a.questionNumber, { value: a.value, flagged: a.flaggedForReview });
    }
    return map;
  }, [review]);

  const marks = useMemo(() => {
    const map = new Map<number, ReviewMark>();
    for (const m of review?.marks ?? []) map.set(m.questionNumber, m);
    return map;
  }, [review]);

  const part = review?.parts.find((p) => p.index === partIndex) ?? review?.parts[0] ?? null;

  /**
   * The evidence, drawn with the same code that draws a candidate's own
   * highlights — the offsets mean the same thing, so they can.
   *
   * Re-applied after every render, because the passage is re-rendered whenever
   * the part or the chosen question changes and anything written into the DOM
   * is thrown away with it.
   */
  useEffect(() => {
    const root = passageRef.current;
    if (!root) return;
    unpaint(root);
    const mark = showing === null ? null : marks.get(showing);
    if (!mark?.evidence || mark.evidencePart !== part?.index) return;
    paint(root, [
      { id: `evidence-${mark.questionNumber}`, start: mark.evidence.start, end: mark.evidence.end },
    ]);

    // The passage scrolls on its own, so a sentence in paragraph F would be
    // highlighted out of sight. Bring it to a third of the way down the pane —
    // the pane only, not the page, which the candidate may have scrolled on
    // purpose to read the question.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const first = root.querySelector<HTMLElement>('.xmark');
    if (first) {
      const offset = first.getBoundingClientRect().top - root.getBoundingClientRect().top;
      root.scrollTo({
        top: root.scrollTop + offset - root.clientHeight / 3,
        behavior: still ? 'auto' : 'smooth',
      });
    }

    // On a phone the sentence also opens under the question that was tapped,
    // and the question is often the last thing on screen — so the sentence
    // opened just below the bottom edge and nothing seemed to happen. Brought
    // in as far as it needs to be and no further. On a wide screen it is not
    // drawn at all, and this moves nothing.
    document
      .querySelector<HTMLElement>('.review__quote')
      ?.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' });
  }, [showing, marks, part]);

  if (error) return <Banner tone="error">{error}</Banner>;
  if (!review || !part) return <p className="muted">Loading…</p>;
  if (review.writing) return <WritingReviewView review={review} id={id} />;

  const slots = part.groups.flatMap((group) => {
    const found = headingSlots(group);
    return found ? found.map((slot) => ({ ...slot, groupId: group.id })) : [];
  });
  const inPassage = slots.length > 0 && passageHasParagraphs(part.passageHtml ?? null, slots);
  const bank = new Map<string, BankOption>();
  for (const group of part.groups) {
    for (const option of group.optionsBank ?? []) bank.set(option.letter, option);
  }

  const evidenceCount = review.marks.filter((m) => m.evidence).length;
  const listening = review.test.module === 'LISTENING';
  const recording = listening ? recordingFor(review, part.index) : null;

  return (
    <>
      <PageHeader
        eyebrow={<Link to={review.showsAnswers ? `/results/${id}` : `/my/results/${id}`}>← Result</Link>}
        title={review.test.title}
        meta={
          <p className="muted">
            {review.showsAnswers ? review.candidate.fullName : 'You'} ·{' '}
            {review.result.score !== null && <>{review.result.score} correct · </>}
            band {review.result.band ?? '—'}
          </p>
        }
      />

      {/* What to practise, rather than a list of what was missed. */}
      <section className="review__tally review__tally--types">
        {review.byType.map((t) => (
          <article
            key={t.type}
            className="review__tallyItem"
            style={{ '--share': t.asked ? t.correct / t.asked : 0 } as CSSProperties}
          >
            <p className="review__tallyLabel">{TYPE_LABEL[t.type] ?? t.type}</p>
            <p className="review__tallyScore">
              {t.correct}<span className="muted">/{t.asked}</span>
            </p>
          </article>
        ))}
      </section>

      <div className="listbar">
        <div className="groupbar__chips">
          <span className="eyebrow">Part</span>
          {review.parts.map((p) => (
            <button
              key={p.index}
              className={`chip ${p.index === partIndex ? 'chip--on' : ''}`}
              onClick={() => {
                setPartIndex(p.index);
                setShowing(null);
              }}
            >
              {p.index}
            </button>
          ))}
        </div>
        {!listening && (
          <p className="muted" style={{ marginLeft: 'auto' }}>
            {evidenceCount} of {review.marks.length} questions show where the answer came from
          </p>
        )}
      </div>

      {listening && recording && (
        <Recording
          key={recording.url}
          url={recording.url}
          label={recording.label}
          durationMs={recording.durationMs}
          speed={speed}
          onSpeed={setSpeed}
          note={
            recording.wholePaper
              ? 'This paper has one recording for all four parts, so it starts at Part 1 whichever part is on screen.'
              : null
          }
        />
      )}
      {listening && !recording && (
        <p className="muted">There is no recording for this part.</p>
      )}

      <div className={part.passageHtml ? 'review review--split' : 'review'}>
        {part.passageHtml && (
          <div className="review__passage" ref={passageRef}>
            <PassagePane
              html={part.passageHtml}
              slots={inPassage ? slots : []}
              bank={bank}
              answers={answers}
              current={showing ?? 0}
              disabled
              onChange={LOCKED}
              onFocusQuestion={LOCKED}
            />
          </div>
        )}

        <div className="review__questions">
          {part.groups.map((group) => (
            <div key={group.id} className="review__group">
              <ExamGroup
                group={group}
                answers={answers}
                current={showing ?? 0}
                disabled
                onChange={LOCKED}
                onFocusQuestion={LOCKED}
                headingsInPassage={inPassage}
                module={review.test.module}
              />
              <ul className="review__marks">
                {group.questions.map((q) => {
                  const mark = marks.get(q.number);
                  if (!mark) return null;
                  const open = showing === q.number;
                  return (
                    <li
                      key={q.number}
                      className={`review__mark ${mark.correct ? 'is-right' : 'is-wrong'}`}
                    >
                      <span className="review__markNum">{q.number}</span>
                      <span className="review__markGiven">
                        {mark.given?.trim() ? mark.given : <em className="muted">nothing written</em>}
                      </span>
                      {review.showsAnswers && mark.accepted.length > 0 && (
                        <span className="review__markKey">{mark.accepted.join(' / ')}</span>
                      )}
                      {/* A Listening paper has no passage to point into: the
                          recording above is where its answers came from, and
                          forty rows saying "no evidence recorded" would say
                          the opposite. */}
                      {listening ? null : mark.evidence && mark.evidencePart === part.index ? (
                        <>
                          <button
                            className="linkbtn"
                            aria-pressed={open}
                            onClick={() => setShowing(open ? null : q.number)}
                          >
                            {open ? 'Hide' : 'Where this came from'}
                          </button>
                          {/* On a phone the passage is a screen away from the
                              question, so the sentence is also shown here,
                              where the candidate tapped. On a wide screen the
                              passage is beside the question and this is hidden:
                              it would say the same thing twice. */}
                          {open && <blockquote className="review__quote">{mark.evidence.quote}</blockquote>}
                        </>
                      ) : (
                        /* Said plainly rather than left blank: a True/False
                           question has no sentence a rule can point at, and a
                           gap where an explanation should be reads as broken. */
                        <span className="muted review__noEvidence">no evidence recorded</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
